import React, { useEffect } from 'react';
import { styled } from '@mui/material';
import { DragDropContext, Droppable } from 'react-beautiful-dnd';

import { KanbanTaskCard } from 'components/organisms/Task';
import { useAuth } from 'contexts/auth';
import { Box, Button, ButtonGroup, Container, Icon, IconButton, Stack, Text, TextInput } from 'shell/components';
import { getTooltipComponent } from 'utils/helper';
import { useTour } from 'contexts/tour';

const Mobile = ({
    viewOptions,
    view,
    setView,
    taskList,
    columns = [],
    isSorted,
    numFilters,
    searchBarInput,
    onChangeSearchBarInput,
    openCreateTaskDrawer,
    openFilterDrawer,
    onDragEnd,
    onOpenTask,
    onDeleteTask,
    onDuplicateTask,
    updateTask,

    tooltipTexts,
}) => {
    const { auth } = useAuth();
    // const {currentStep} = useTour();

    // useEffect(() => {
    //     if (currentStep === 8) {
    //         provided
    //     }
    // }, [currentStep])

    const PageInner = (
        <RootStack>
            <Stack spacing={3}>
                <ButtonGroup options={viewOptions} selected={view} onChange={setView} />
                <FiltersStack direction={'row'} spacing={2}>
                    <Button
                        color={'neutral'}
                        startIcon={<Icon kind={'filter'} />}
                        variant={'tertiary'}
                        onClick={openFilterDrawer}
                    >
                        Filters {numFilters ? `(${numFilters})` : ''}
                    </Button>
                    <TextInput
                        sx={{ flex: 1 }}
                        disablePointerEventsOnIcon={!Boolean(searchBarInput)}
                        endIcon={
                            searchBarInput ? (
                                <IconButton
                                    aria-label={'clear search input'}
                                    kind={'exit'}
                                    variant={'square'}
                                    onClick={() => onChangeSearchBarInput('')}
                                    onMouseDown={(e) => e.preventDefault()}
                                />
                            ) : (
                                <Icon kind={'search'} />
                            )
                        }
                        placeholder={'Search'}
                        value={searchBarInput}
                        onChange={(e) => onChangeSearchBarInput(e.target.value)}
                    />
                    <Button
                        className={'task-3'}
                        startIcon={<Icon kind={'add'} />}
                        onClick={() => openCreateTaskDrawer('toDo')}
                    >
                        New Task
                    </Button>
                </FiltersStack>
            </Stack>
            <TaskBoardStack className={'task-1'} direction={'row'} spacing={4}>
                <DragDropContext onDragEnd={onDragEnd}>
                    {columns.map(({ droppableId, title, tasks }) => (
                        <ColumnRootStack key={droppableId}>
                            <ColumnHeaderStack alignItems={'center'} direction={'row'} justifyContent={'space-between'}>
                                <Text
                                    tooltip={{
                                        component: getTooltipComponent(
                                            tooltipTexts[droppableId].title,
                                            tooltipTexts[droppableId].description
                                        ),
                                        maxWidth: '10rem',
                                    }}
                                    variant={'sh2'}
                                    weight={'semibold'}
                                >
                                    {title} {tasks.length ? `(${tasks.length})` : ''}
                                </Text>
                            </ColumnHeaderStack>
                            <ColumnOuterBox isInstructor={auth.role === 'instructor'}>
                                <Droppable droppableId={droppableId}>
                                    {(provided, snapshot) => (
                                        <ColumnInnerStack
                                            data-cy={title}
                                            ref={provided.innerRef}
                                            className={'taskCard'}
                                            isDraggingOver={snapshot.isDraggingOver}
                                            isSorted={isSorted}
                                            {...provided.droppableProps}
                                        >
                                            {tasks.map((task, index) => (
                                                <KanbanTaskCard
                                                    key={task.id}
                                                    index={index}
                                                    task={task}
                                                    isSorted={isSorted}
                                                    onOpenTask={onOpenTask}
                                                    onDeleteTask={onDeleteTask}
                                                    onDuplicateTask={onDuplicateTask}
                                                    updateTask={updateTask}
                                                />
                                            ))}
                                            {provided.placeholder}
                                        </ColumnInnerStack>
                                    )}
                                </Droppable>
                            </ColumnOuterBox>
                        </ColumnRootStack>
                    ))}
                </DragDropContext>
            </TaskBoardStack>
        </RootStack>
    );

    return auth.role === 'instructor' ? (
        <Box sx={{ height: 'calc(100vh - 174px)' }}>{PageInner}</Box>
    ) : (
        <Container sx={{ pb: 0 }} device={'mobile'}>
            {PageInner}
        </Container>
    );
};

const RootStack = styled(Stack)(() => {
    return {
        width: '100%',
        height: '100%',
    };
});

const FiltersStack = styled(Stack)(() => {
    return {
        width: '100%',
    };
});

const TaskBoardStack = styled(Stack)(({ theme }) => {
    return {
        margin: theme.spacing(0, -5),
        height: '100%',
        padding: theme.spacing(4, 5, 0),
        overflowX: 'auto',
        overflowY: 'hidden',
    };
});

const ColumnRootStack = styled(Stack)(() => {
    return {
        minWidth: 332,
        maxWidth: 332,
        height: '100%',
    };
});

const ColumnHeaderStack = styled(Stack)(({ theme }) => {
    return {
        marginBottom: theme.spacing(2),
    };
});

const ColumnOuterBox = styled(Box)(({ isInstructor, theme }) => {
    return {
        borderTopLeftRadius: '12px',
        borderTopRightRadius: '12px',
        height: 'calc(100vh - 226px)',
        padding: theme.spacing(3, 3, 0),
        background: theme.palette.ensightful.shades[0],
        ...(isInstructor && {
            height: 'calc(100vh - 316px)',
        }),
    };
});

const ColumnInnerStack = styled(Stack)(({ isDraggingOver, isSorted, theme }) => {
    return {
        margin: theme.spacing(-3),
        borderTopLeftRadius: '12px',
        borderTopRightRadius: '12px',
        height: 'calc(100% + 12px)',
        padding: theme.spacing(3),
        overflowY: 'auto',
        ...(isDraggingOver && {
            background: theme.palette.ensightful.primary[50],
            ...(isSorted && {
                border: `2px dashed ${theme.palette.ensightful.primary[100]}`,
                padding: theme.spacing(2.5),
            }),
        }),
    };
});

export default Mobile;
